/**
 * Pin the app to a specific published bundle.
 *
 * `?bundle=<sha>` names a build; the choice is remembered so it survives
 * navigation, and dock-one is told about it on the next document request. The
 * point is reproducing a report against the exact bundle that produced it,
 * rather than whatever is current.
 *
 * It travels as a cookie because the thing being overridden is the DOCUMENT
 * request — a top-level navigation, which a page cannot add headers to.
 * localStorage holds the same value only so the UI can tell you that you are
 * pinned; the cookie is what dock-one reads.
 *
 * dock-one refuses the override in production (see config.AllowBundleOverride):
 * a link carrying an old sha is a downgrade attack, re-serving client-side bugs
 * that were already fixed to whoever opens it. Nothing here can grant what the
 * server declines, which is the right place for that decision to live.
 */ const PARAM = 'bundle';
export const BUNDLE_COOKIE = 'dockone_bundle';
export const BUNDLE_STORAGE_KEY = 'dockone.bundleOverride';
/** A content address, matching what dock-one will accept. */ const SHA = /^[0-9a-f]{7,64}$/;
/**
 * The pinned sha, or null. Reads the cookie, which is the authority.
 *
 * decodeURIComponent throws on malformed percent encoding (`%E0%A4%A`), and
 * this runs during boot — so a corrupted or hand-edited cookie would take the
 * whole app down rather than merely failing to pin. Anything undecodable is
 * treated as no override, which is the same answer as any other unusable value.
 */ export function currentBundleOverride() {
    const match = document.cookie.match(/(?:^|;\s*)dockone_bundle=([^;]*)/);
    if (!match?.[1]) {
        return null;
    }
    let value;
    try {
        value = decodeURIComponent(match[1]);
    } catch  {
        return null;
    }
    return SHA.test(value) ? value : null;
}
function writeCookie(value) {
    // Session-scoped and path-wide: it must ride every document request, and it
    // should not outlive the browser session that set it — a pin is for a
    // debugging session, not a permanent state someone forgets they are in.
    document.cookie = value ? `${BUNDLE_COOKIE}=${encodeURIComponent(value)}; path=/; SameSite=Lax` : `${BUNDLE_COOKIE}=; path=/; Max-Age=0; SameSite=Lax`;
}
/**
 * Applies `?bundle=` from the URL, if present.
 *
 * Returns true when the caller should stop and let the reload happen: the
 * running page was built from the old bundle, so honouring a new pin means
 * fetching the document again rather than continuing to boot.
 *
 * `?bundle=` with no value clears the pin, so getting unstuck never requires
 * knowing where the state is kept.
 */ export function applyBundleOverrideFromURL() {
    let url;
    try {
        url = new URL(window.location.href);
    } catch  {
        return false;
    }
    const requested = url.searchParams.get(PARAM);
    if (requested === null) {
        return false;
    }
    const sha = requested.trim().toLowerCase();
    if (sha !== '' && !SHA.test(sha)) {
        // Not a content address. Strip it rather than reloading into the same bad
        // param forever.
        url.searchParams.delete(PARAM);
        window.history.replaceState(null, '', url.toString());
        return false;
    }
    if (sha === currentBundleOverride() || sha === '' && currentBundleOverride() === null) {
        // Already in the requested state; drop the param and carry on booting so a
        // shared link does not reload on every visit.
        url.searchParams.delete(PARAM);
        window.history.replaceState(null, '', url.toString());
        return false;
    }
    writeCookie(sha);
    try {
        if (sha) {
            window.localStorage.setItem(BUNDLE_STORAGE_KEY, sha);
        } else {
            window.localStorage.removeItem(BUNDLE_STORAGE_KEY);
        }
    } catch  {
    // Private windows and blocked site data. The cookie is what matters; this
    // is only so the UI can say which bundle you are on.
    }
    // Reload without the param, so the pin is not re-applied on every navigation
    // and the address bar stops advertising it.
    url.searchParams.delete(PARAM);
    window.location.replace(url.toString());
    return true;
}
